import React, { useEffect, useState, useRef } from 'react'
import groupBy from 'lodash.groupby'
import uniqWith from 'lodash.uniqwith'
import partition from 'lodash.partition'

import {
  Spinner,
  Text,
  Tooltip,
  Popover,
  downloadFile,
} from '@sketch/components'

// Utils
import { useHover, usePrevious } from '@sketch/utils'

// Constants
import { SKETCH_WEBSITE } from '@sketch/constants'

// Icons
import { ReactComponent as ExportAssetsIcon } from '@sketch/icons/arrow-down-to-line-16-svg'

// Components
import { ExportFormat, Layer, VisibleScaleType } from 'modules/shares/types'
import { InspectorData } from 'modules/shares/Inspector/types'

// Local Components
import Separator from '../Separator'
import Header, { Title } from '../Header'
import Attribute, { Label, AttributeList } from '../Attribute'

// Types
import {
  VersionFragment,
  useRenderDownloadableAssetsMutation,
} from '@sketch/gql-types'

// Styles
import {
  ExportValue,
  ExportsSection,
  DownloadFormatButton,
  SectionStyled,
  QuestionMark,
  NoAssetsAvailableTitle,
  NoAssetsAvailableTooltip,
  Error,
  AllFormatsSeparator,
  ExportButtonWrapper,
} from './Export.styles'

//-----------------------------------------------------------------------------
// UTILS

const isSVGExport = (xport: ExportFormat): boolean =>
  xport.fileFormat.toLowerCase() === 'svg'

const isAxisConstrainedExport = (xport: ExportFormat): boolean =>
  xport.visibleScaleType === VisibleScaleType.width ||
  xport.visibleScaleType === VisibleScaleType.height

const stringForExport = (xport: ExportFormat): string => {
  if (xport.fileFormat.toLowerCase() === 'svg') {
    return 'Scalable'
  }

  switch (xport.visibleScaleType) {
    case VisibleScaleType.width:
      return `${xport.absoluteSize}w`

    case VisibleScaleType.height:
      return `${xport.absoluteSize}h`

    case VisibleScaleType.scale:
      return `${xport.scale}x`
  }
}

const stringForExports = (exports: ExportFormat[]): string => {
  const [axisConstrainedExports, scaledExports] = partition(
    uniqWith(exports, isSVGExport),
    isAxisConstrainedExport
  )

  const axisStrings = axisConstrainedExports
    .sort((a, b) => a.absoluteSize - b.absoluteSize)
    .sort((a, b) => a.visibleScaleType - b.visibleScaleType)

  const scaledStrings = scaledExports.sort((a, b) => a.scale - b.scale)

  return [...scaledStrings, ...axisStrings].map(stringForExport).join(', ')
}

type ErrorType = 'default' | 'forbidden' | undefined

interface IndividualAssetsProps {
  layerOrArtboardId: string
  formats?: ExportFormat['fileFormat'][]
  scales?: number[]
  currentVersion: VersionFragment
}

// React hook to manage the logic to request individual assets
const useRequestIndividualAssets = ({
  layerOrArtboardId,
  formats = [],
  scales = [],
  currentVersion,
}: IndividualAssetsProps) => {
  // Mutation to request individual assets, for now this process is
  // synchronously for layer assets so it will return the asset ready to download
  const [
    renderAssets,
    { data, loading, error },
  ] = useRenderDownloadableAssetsMutation({
    redirectErrors: false,
    variables: {
      documentIdentifier: currentVersion.document?.identifier || '',
      input: {
        layerUuids: [layerOrArtboardId],
        formats,
        scales,
      },
    },
    onError: 'unsafe-throw-exception',
  })

  const shouldShowError =
    (error || data?.renderDownloadableAssets?.errors.length) && !loading

  const errorType: ErrorType = shouldShowError
    ? (data?.renderDownloadableAssets?.errors || []).find(
        error => error?.code === 'FORBIDDEN'
      )
      ? 'forbidden'
      : 'default'
    : undefined

  const asset = data?.renderDownloadableAssets?.downloadableAsset
  const previousAsset = usePrevious(asset)

  // Automatically downloads the asset when it's ready
  useEffect(() => {
    if (asset?.path && !previousAsset && !shouldShowError) {
      downloadFile(asset!.path!)
    }
  }, [asset, previousAsset, shouldShowError])

  return { renderAssets, loading, errorType }
}

function isSelectedLayerExportable(selection: Selection | undefined): boolean {
  return (
    selection?.type === 'layer' && !!selection?.value?.exportFormats?.length
  )
}

//-----------------------------------------------------------------------------
// COMPONENTS:
//  - ErrorText: shows different types of errors when downloading an asset
//  - ExportButton: button to download an individual asset
//  - Export: download info for every format (title, value and button)
//  - ExportAll: same as Export but to export all formats from an individual asset
//  - MakeExportable: info with tooltip shown when a layer has no assets exported
//  - Exports: exported component that builds the rest of the pieces

interface ErrorTextProps {
  type: ErrorType
}

const ErrorText: React.FC<ErrorTextProps> = ({ type = 'default' }) => {
  const text = {
    default:
      'Failed to export assets. Something went wrong. Try uploading the document again',
    forbidden:
      'The option to export assets has been disabled in this document’s settings. Please contact the document’s owner for assistance.',
  }

  return (
    <Text color="red" marginBottom={0} textAlign="center">
      {text[type]}
    </Text>
  )
}

interface ExportButtonProps {
  loading: boolean
  onClick: () => void
}

const ExportButton: React.FC<ExportButtonProps> = ({
  onClick,
  loading,
  ...props
}) => (
  <>
    <DownloadFormatButton
      disabled={loading}
      onClick={() => !loading && onClick()}
      data-testid="download-asset-request"
      {...props}
    >
      {loading ? (
        <Spinner primary size="12px" />
      ) : (
        <ExportAssetsIcon width={17} height={17} color="grey" />
      )}
    </DownloadFormatButton>
  </>
)

interface ExportProps
  extends Pick<IndividualAssetsProps, 'layerOrArtboardId' | 'currentVersion'> {
  format: ExportFormat['fileFormat']
  exportByName: ExportFormat[]
}

const Export = ({
  format,
  exportByName,
  currentVersion,
  layerOrArtboardId,
}: ExportProps) => {
  const { renderAssets, loading, errorType } = useRequestIndividualAssets({
    layerOrArtboardId: layerOrArtboardId,
    formats: [format.toUpperCase()] as ExportFormat['fileFormat'][],
    scales: exportByName
      ? // Note: this is just simple way to keep unique items: `Array.from(new Set([1,2,2]))` returns [1,2]
        Array.from(new Set(exportByName.map(e => e.scale)))
      : [],
    currentVersion,
  })

  const Wrapper = errorType ? Error : React.Fragment

  return (
    <Wrapper>
      <Attribute key={format}>
        <Label>{format.toUpperCase()}</Label>
        <ExportValue>{stringForExports(exportByName)}</ExportValue>
        <ExportButtonWrapper>
          <ExportButton onClick={renderAssets} loading={loading} />
        </ExportButtonWrapper>
        {errorType && <ErrorText type={errorType} />}
      </Attribute>
    </Wrapper>
  )
}

const ExportAll = ({
  currentVersion,
  layerOrArtboardId,
}: Pick<IndividualAssetsProps, 'layerOrArtboardId' | 'currentVersion'>) => {
  const { renderAssets, loading, errorType } = useRequestIndividualAssets({
    layerOrArtboardId,
    currentVersion,
  })

  const Wrapper = errorType ? Error : React.Fragment

  return (
    <Wrapper>
      <Attribute>
        <Label>
          <strong>All Formats</strong>
        </Label>
        <ExportValue />
        <ExportButtonWrapper>
          <Tooltip content="Download all available formats for this layer">
            <ExportButton
              onClick={renderAssets}
              loading={loading}
              data-testid="download-asset-all-request"
            />
          </Tooltip>
        </ExportButtonWrapper>
        {errorType && <ErrorText type={errorType} />}
      </Attribute>
    </Wrapper>
  )
}

type Selection =
  | { type: 'layer'; value: Layer }
  | { type: 'artboard'; value: InspectorData }

const selectionTypeName = {
  layer: 'layer',
  artboard: 'Artboard',
}

interface NoAssetsAvailableProps {
  selection?: Selection
}
const NoAssetsAvailable = ({ selection }: NoAssetsAvailableProps) => {
  const [hovered, hoverEventListeners] = useHover()

  // We need more control to show or hide the tooltip, because a small
  // delay is needed to close it (to let the user reach the tooltip from the trigger)
  const [visible, setVisibility] = useState(false)

  // This ref helps to avoid closing the tooltip if the user is over it
  const shouldIgnoreClose = useRef(false)

  useEffect(() => {
    if (!hovered) {
      // transitioning from visible to hidden
      setTimeout(() => {
        if (!shouldIgnoreClose.current) setVisibility(false)
      }, 100)
    } else {
      setVisibility(true)
    }
  }, [hovered])

  return (
    <SectionStyled>
      <Header>
        <Popover
          spacing="18px"
          style={{
            height: '18px',
          }}
          visible={visible}
          useOnClickOutside={() => setVisibility(false)}
          popup={
            <NoAssetsAvailableTooltip
              onMouseEnter={() => (shouldIgnoreClose.current = true)}
              onMouseLeave={() => {
                shouldIgnoreClose.current = false
                setVisibility(false)
              }}
            >
              To download assets, first make this{' '}
              {selectionTypeName[selection?.type ?? 'artboard']} exportable in
              the Mac app.{' '}
              <a
                href={`${SKETCH_WEBSITE}/docs/exporting/#how-to-create-and-use-export-presets`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </NoAssetsAvailableTooltip>
          }
        >
          <NoAssetsAvailableTitle {...hoverEventListeners}>
            No assets available <QuestionMark />
          </NoAssetsAvailableTitle>
        </Popover>
      </Header>
    </SectionStyled>
  )
}

interface ExportsProps extends Pick<IndividualAssetsProps, 'currentVersion'> {
  for?: Selection
  hasArtboardExportableAssets?: boolean
}

const Exports = ({
  for: selection,
  currentVersion,
  hasArtboardExportableAssets,
}: ExportsProps) => {
  const formats = selection?.value.exportFormats
  const hasSelectionExports = !!formats?.length
  const exportsByName = groupBy(formats, 'fileFormat')
  const formatNames = Object.keys(exportsByName)

  const hasSomeAssetsAvailable =
    hasArtboardExportableAssets && !isSelectedLayerExportable(selection)

  const hasNoAssetsAvailable =
    !hasArtboardExportableAssets &&
    !isSelectedLayerExportable(selection) &&
    !hasSelectionExports

  const exportableAssetId = selection?.value.exportID || selection?.value.id

  return (
    <>
      <Separator />
      {hasSomeAssetsAvailable && null}
      {hasNoAssetsAvailable && <NoAssetsAvailable selection={selection} />}
      {hasSelectionExports && (
        <ExportsSection data-testid="inspector-sidebar-exports">
          <Header>
            <Title>Assets</Title>
          </Header>
          <AttributeList>
            {formatNames.map(format => (
              <Export
                key={format}
                format={format as ExportFormat['fileFormat']}
                exportByName={exportsByName[format]}
                layerOrArtboardId={exportableAssetId as string}
                currentVersion={currentVersion}
              />
            ))}
            {formatNames.length > 1 && (
              <>
                <AllFormatsSeparator />
                <ExportAll
                  currentVersion={currentVersion}
                  layerOrArtboardId={exportableAssetId as string}
                />
              </>
            )}
          </AttributeList>
        </ExportsSection>
      )}
    </>
  )
}

export default Exports
